import { cn } from '@lib/cn';

import type { ReactNode } from 'react';

/* ------------------------------------------------------------------ Badge - */

export type BadgeTone =
    | 'ga'
    | 'active'
    | 'success'
    | 'beta'
    | 'trial'
    | 'warning'
    | 'dev'
    | 'info'
    | 'deprecated'
    | 'suspended'
    | 'neutral'
    | 'danger'
    | 'brand';

const TONE_CLASSES: Record<BadgeTone, { badge: string; dot: string }> = {
    ga: {
        badge: 'bg-success-surface text-success-muted border-success-line',
        dot: 'bg-success-solid',
    },
    active: {
        badge: 'bg-success-surface text-success-muted border-success-line',
        dot: 'bg-success-solid',
    },
    success: {
        badge: 'bg-success-surface text-success-muted border-success-line',
        dot: 'bg-success-solid',
    },
    beta: {
        badge: 'bg-warning-surface text-warning-muted border-warning-line',
        dot: 'bg-warning-solid',
    },
    trial: {
        badge: 'bg-warning-surface text-warning-muted border-warning-line',
        dot: 'bg-warning-solid',
    },
    warning: {
        badge: 'bg-warning-surface text-warning-muted border-warning-line',
        dot: 'bg-warning-solid',
    },
    dev: { badge: 'bg-info-surface text-info-muted border-info-line', dot: 'bg-info-solid' },
    info: { badge: 'bg-info-surface text-info-muted border-info-line', dot: 'bg-info-solid' },
    deprecated: {
        badge: 'bg-surface-inset text-content-subtle border-line',
        dot: 'bg-content-faint',
    },
    suspended: {
        badge: 'bg-surface-inset text-content-subtle border-line',
        dot: 'bg-content-faint',
    },
    neutral: { badge: 'bg-surface-inset text-content-subtle border-line', dot: 'bg-content-faint' },
    danger: {
        badge: 'bg-danger-surface text-danger-muted border-danger-line',
        dot: 'bg-danger-solid',
    },
    brand: { badge: 'bg-accent-surface text-accent-text border-accent-line', dot: 'bg-brand' },
};

export function Badge({
    tone = 'neutral',
    dot = false,
    children,
    className,
}: {
    tone?: BadgeTone;
    dot?: boolean;
    children: ReactNode;
    className?: string;
}) {
    const t = TONE_CLASSES[tone];
    return (
        <span
            className={cn(
                'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-0.5 text-[11px] font-semibold',
                t.badge,
                className,
            )}
        >
            {dot && <span className={cn('size-1.5 rounded-full', t.dot)} />}
            {children}
        </span>
    );
}

/* ------------------------------------------------------------- FlagKeyPill - */

export function FlagKeyPill({ children }: { children: ReactNode }) {
    return (
        <span className="inline-block rounded-md bg-surface-inset px-1.5 py-0.5 font-mono text-[12.5px] text-content-strong">
            {children}
        </span>
    );
}

/* ------------------------------------------------------------------- Card - */

export function Card({ children, className }: { children: ReactNode; className?: string }) {
    return (
        <div
            className={cn(
                'overflow-hidden rounded-xl border border-line bg-surface shadow-card',
                className,
            )}
        >
            {children}
        </div>
    );
}

export function CardHeader({
    title,
    subtitle,
    action,
}: {
    title: ReactNode;
    subtitle?: ReactNode;
    action?: ReactNode;
}) {
    return (
        <div className="flex items-center gap-3 border-b border-line-subtle px-5 py-3.5">
            <h3 className="text-[15px] font-semibold text-content">{title}</h3>
            {subtitle && <span className="text-[12.5px] text-content-muted">{subtitle}</span>}
            {action && <div className="ml-auto">{action}</div>}
        </div>
    );
}

/**
 * One labelled fact, sized for the 320px sidecar rather than for a form.
 *
 * Label left, value right, hairline between — the shape a reader scans down
 * without reading, which is what a metadata rail is for. It is deliberately not
 * the `<dl>` grid the wider cards use: that one gives the label a fixed 190px
 * column, which in a narrow rail leaves the value nowhere to go.
 */
export function KeyValue({ label, children }: { label: string; children: ReactNode }) {
    return (
        <div className="flex items-center justify-between gap-3 border-b border-line-subtle py-2.5 text-[13px] last:border-0">
            <span className="shrink-0 text-content-muted">{label}</span>
            <span className="truncate text-right font-semibold text-content-strong">
                {children}
            </span>
        </div>
    );
}

/* ------------------------------------------------------------------- Chip - */

/**
 * Renders as a `<button>` when given an `onClick` and a `<span>` otherwise.
 *
 * The filter chips are now real controls, but several chips are still pure
 * labels ("Overridden 3"). Emitting a button for those would put unlabelled,
 * focusable, do-nothing stops in the keyboard order of every screen that shows
 * a count.
 */
export function Chip({
    active = false,
    onClick,
    children,
    className,
}: {
    active?: boolean;
    onClick?: () => void;
    children: ReactNode;
    className?: string;
}) {
    const classes = cn(
        'inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-[12.5px] font-medium transition-colors',
        active
            ? 'border-accent-line bg-accent-surface text-accent-text'
            : 'border-line bg-surface text-content-subtle',
        onClick && !active && 'hover:bg-surface-sunken',
        className,
    );

    if (!onClick) return <span className={classes}>{children}</span>;

    return (
        <button type="button" onClick={onClick} aria-pressed={active} className={classes}>
            {children}
        </button>
    );
}

/* ----------------------------------------------------------------- Button - */

/**
 * `onClick`, `type` and `disabled` are passed through rather than the whole of
 * `ButtonHTMLAttributes`: the point of this component is that every button in
 * the app looks the same, and spreading arbitrary props would let a call site
 * pass its own `className` twice or override the styling wholesale.
 *
 * `disabled` is styled, not just set. An in-flight write disables its button,
 * and a control that looks live but ignores clicks reads as a broken app.
 */
export function Button({
    variant = 'primary',
    size = 'md',
    type = 'button',
    onClick,
    disabled = false,
    children,
    className,
}: {
    variant?: 'primary' | 'ghost' | 'danger';
    size?: 'md' | 'sm';
    type?: 'button' | 'submit';
    onClick?: () => void;
    disabled?: boolean;
    children: ReactNode;
    className?: string;
}) {
    // `enabled:hover:` rather than `hover:` cancelled by a conditional class:
    // `cn` is a plain joiner, so two competing `hover:bg-*` utilities would be
    // decided by stylesheet order, not by which one this component listed last.
    //
    // `text-white` is deliberately literal here (and on the other terracotta
    // and red solids in the app): it is the foreground *of the brand fill*,
    // which is the same colour in both themes, so a token that follows the
    // theme would turn the label near-black on a terracotta button.
    const VARIANT_CLASSES = {
        primary: 'border-transparent bg-brand text-white shadow-sm enabled:hover:bg-brand-600',
        ghost: 'border-line bg-surface text-content-body enabled:hover:bg-surface-sunken',
        danger: 'border-danger-line bg-surface text-danger-muted enabled:hover:bg-danger-surface',
    } as const;

    return (
        <button
            type={type === 'submit' ? 'submit' : 'button'}
            onClick={onClick}
            disabled={disabled}
            className={cn(
                'inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg border font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50',
                size === 'sm' ? 'px-3 py-1.5 text-[12.5px]' : 'px-4 py-2 text-[13.5px]',
                VARIANT_CLASSES[variant],
                className,
            )}
        >
            {children}
        </button>
    );
}

/* --------------------------------------------------------------- StatTile - */

export function StatTile({
    icon,
    iconClassName,
    label,
    value,
    hint,
    hintTone = 'flat',
}: {
    icon?: ReactNode;
    iconClassName?: string;
    label: string;
    value: ReactNode;
    hint?: ReactNode;
    hintTone?: 'up' | 'flat';
}) {
    return (
        <div className="rounded-xl border border-line bg-surface p-[18px] shadow-card">
            <div className="flex items-center gap-2 text-xs font-medium text-content-muted">
                {icon && (
                    <span
                        className={cn(
                            'flex size-7 items-center justify-center rounded-lg',
                            iconClassName ?? 'bg-surface-inset text-content-muted',
                        )}
                    >
                        {icon}
                    </span>
                )}
                {label}
            </div>
            <div className="mt-2 text-[28px] font-semibold tracking-tight text-content">
                {value}
            </div>
            {hint && (
                <div
                    className={cn(
                        'mt-1.5 text-xs font-semibold',
                        hintTone === 'up' ? 'text-success-icon' : 'text-content-faint',
                    )}
                >
                    {hint}
                </div>
            )}
        </div>
    );
}

/* ------------------------------------------------------------- PageHeader - */

export function PageHeader({
    eyebrow,
    title,
    description,
    actions,
}: {
    eyebrow?: string;
    title: ReactNode;
    description?: ReactNode;
    actions?: ReactNode;
}) {
    return (
        <div className="mb-6 flex items-start gap-4">
            <div>
                {eyebrow && (
                    <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-accent-icon">
                        {eyebrow}
                    </div>
                )}
                <h1 className="text-2xl font-semibold tracking-tight text-content">{title}</h1>
                {description && (
                    <p className="mt-1 max-w-2xl text-[13.5px] text-content-muted">{description}</p>
                )}
            </div>
            {actions && <div className="ml-auto flex shrink-0 gap-2.5">{actions}</div>}
        </div>
    );
}
